import { Link } from 'react-router-dom';
import { Target, Users, Zap, TrendingUp } from 'lucide-react';

export default function BlogPolymarketCopyTrading() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        {/* Header */}
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-600/10 border border-primary-500/30 text-xs text-primary-200 uppercase tracking-wide">
            <TrendingUp className="w-3 h-3" />
            <span>Polymarket Copy Trading</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Turn Elite Polymarket Traders Into Your Personal Signal Feed With BetMimic
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Polymarket is where sharp prediction markets live – politics, sports, macro and meme events
            priced in real time. BetMimic lets you plug directly into that edge by automatically copying
            the traders you trust from your own wallet, with real risk controls, reconciled positions and
            live PnL that matches what Polymarket itself reports.
          </p>
        </header>

        {/* Section: What is Polymarket and how does it work? */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Quick primer: what is Polymarket?</h2>
          <p className="text-sm text-dark-300">
            Polymarket is a real-money prediction market on Polygon. Instead of betting on a sportsbook,
            you buy and sell <span className="font-medium">YES</span> or <span className="font-medium">NO</span>{' '}
            shares on questions like &quot;Will BTC close above $100k this year?&quot; or &quot;Will Candidate X win the
            US election?&quot; Each share is worth between <span className="font-mono">0</span> and{' '}
            <span className="font-mono">1</span> USDC.e at resolution.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              <span className="font-medium">Price as probability</span> – a YES share at <span className="font-mono">0.63</span>{' '}
              implies a 63% market-implied probability that the event happens.
            </li>
            <li>
              <span className="font-medium">P&amp;L</span> – if you buy YES at <span className="font-mono">0.40</span> and the
              market resolves YES, you get <span className="font-mono">1.00</span>. Your profit is{' '}
              <span className="font-mono">0.60</span> per share (before fees).
            </li>
            <li>
              <span className="font-medium">Order book</span> – Polymarket runs a central limit order book
              (CLOB). You can place limit orders, market-like orders and rest bids/offers before news.
            </li>
          </ul>
          <p className="text-sm text-dark-300">
            That structure makes Polymarket a live scoreboard for everything from US elections and
            interest rate decisions to sports, crypto events and memes. The traders you copy on BetMimic
            are the people constantly repricing those probabilities.
          </p>
        </section>

        {/* Section: Why copy trade Polymarket at all? */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-400" />
            Why copy trade Polymarket at all?
          </h2>
          <p className="text-sm text-dark-300">
            Polymarket has become the reference order book for real-money predictions. The best traders
            there are constantly pricing information about elections, sports, Fed decisions, crypto and
            breaking narratives before CT even notices. Copy trading lets you follow those wallets
            directly, instead of trying to manually react to screenshots and threads.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>Mirror proven traders across every Polymarket market they touch.</li>
            <li>Benefit from their research pipeline, entry timing and exit discipline.</li>
            <li>Stay fully in control of wallet, position sizing and max exposure at all times.</li>
          </ul>
        </section>

        {/* Section: How BetMimic actually executes on Polymarket */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            How BetMimic actually copies traders on Polymarket
          </h2>
          <p className="text-sm text-dark-300">
            BetMimic doesn&apos;t scrape screens or rely on sketchy third‑party APIs. Under the hood, the
            backend uses Polymarket&apos;s <span className="font-medium">official CLOB client</span> – the same
            infrastructure real power users trade on. Every copied order is a real on-chain trade from your
            wallet to the Polymarket order book.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              The engine monitors selected Polymarket wallets in real time and normalizes their trades via
              Polymarket&apos;s Data API (trades, positions, activity).
            </li>
            <li>
              For each wallet you copy, you define a <span className="font-medium">copy config</span> –
              fixed dollar amount, percentage of trader size, or percentage of your own balance.
            </li>
            <li>
              Filters decide which trades are eligible: min price, max price, min trade size, market
              liquidity, volume and more.
            </li>
            <li>
              When a trade passes your filters, BetMimic sends a matching order using the official
              Polymarket CLOB client with your keys and signature type.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            That means fills, fees and PnL all live directly on Polymarket as if you had clicked every
            button yourself – BetMimic just automates the boring &amp; fast parts.
          </p>
        </section>

        {/* Section: Real PnL and position tracking */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Real PnL, not fantasy backtests</h2>
          <p className="text-sm text-dark-300">
            The biggest problem with most &quot;copy bots&quot; is fake accounting: they track a shadow book that
            slowly drifts from reality. BetMimic was built the opposite way. Your Dashboard, Positions page
            and trades feed are all reconciled against what Polymarket itself reports for your wallet.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              Open positions are pulled and verified against Polymarket&apos;s{' '}
              <code className="font-mono">/positions</code> endpoint; if a position is gone on-chain, we
              don&apos;t pretend it still exists.
            </li>
            <li>
              Ghost positions are auto‑closed in the database when they disappear from Polymarket, so the
              UI never shows stale risk.
            </li>
            <li>
              Redeemable markets are automatically claimed so your USDC.e flows back into the wallet and
              into your portfolio / PnL numbers.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            When you look at PnL in BetMimic, you&apos;re effectively looking at a cleaned‑up mirror of what
            Polymarket and Polygon think your wallet owns, not some fantasy spreadsheet.
          </p>
        </section>

        {/* Section: Step-by-step: your first copied trade */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Step-by-step: your first copied trade</h2>
          <p className="text-sm text-dark-300">
            Here&apos;s a concrete flow to go from zero to seeing a copied Polymarket trade show up in your
            BetMimic Positions page and on-chain.
          </p>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-1">
            <li>Connect a funded Polymarket wallet in Settings → Wallet.</li>
            <li>Pick a trader on the Traders page with real volume and a track record, not just vibes.</li>
            <li>Create a copy config with small fixed sizing (e.g. $5–$10 per trade) and low max exposure.</li>
            <li>Enable basic filters: skip tiny trades, skip ultra-illiquid markets, avoid crazy longshots.</li>
            <li>Turn on stop loss / take profit defaults so every new position inherits guardrails.</li>
            <li>Let the engine run and compare your BetMimic trades to what you see on Polymarket.</li>
          </ol>
          <p className="text-xs text-dark-500">
            Once you&apos;re comfortable that copied trades match what you&apos;d do manually, you can scale size,
            add more traders, or build a diversified basket of configs.
          </p>
        </section>

        {/* Section: Common mistakes */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">Common Polymarket copy trading mistakes</h2>
          <p className="text-sm text-dark-300">
            Most bad experiences with &quot;copy trading&quot; come from the same handful of mistakes. Avoid these
            and you&apos;re already ahead of most degens.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              <span className="font-medium">Sizing like the trader, not like yourself</span> – copying a
              whale with your entire bankroll because they went max size is how you nuke your account in
              one sequence.
            </li>
            <li>
              <span className="font-medium">No max exposure</span> – if you don&apos;t cap per-trader risk,
              one hyperactive account can quietly stack a ton of correlated markets.
            </li>
            <li>
              <span className="font-medium">Copying screenshots instead of trades</span> – if you&apos;re
              manually chasing posted fills, you&apos;re always late and probably on the wrong side of the
              spread.
            </li>
            <li>
              <span className="font-medium">Ignoring liquidity</span> – degen niche markets can look
              juicy but have no size. Copy configs let you require minimum liquidity and volume.
            </li>
          </ul>
        </section>

        {/* Section: Who this is for */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-400" />
            Who should be copy trading Polymarket with BetMimic?
          </h2>
          <p className="text-sm text-dark-300">
            BetMimic is built for people who want exposure to information markets but don&apos;t have time to
            live inside the Polymarket order book. If you already follow sharp traders on X or Discord,
            this turns that follow into a structured, risk‑defined strategy.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              Crypto traders who want a hedged macro book without learning every political or sports
              market from scratch.
            </li>
            <li>
              Sports and politics degenerates who would rather outsource execution to people living in
              those markets every day.
            </li>
            <li>
              Builders and founders who want Polymarket exposure but minimal time spent managing tickets.
            </li>
          </ul>
        </section>

        {/* FAQ / Internal links */}
        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">FAQ: Polymarket copy trading with BetMimic</h2>
          <div className="space-y-3 text-sm text-dark-300">
            <div>
              <p className="font-medium">Is this a custodial bot?</p>
              <p>
                No. BetMimic runs on top of <span className="font-medium">your</span> Polymarket wallet. The
                engine uses the keys you provide to sign orders with the official client; funds never sit
                in a pooled bot account.
              </p>
            </div>
            <div>
              <p className="font-medium">Can I still trade manually on Polymarket?</p>
              <p>
                Yes. Manual trades and copied trades both show up in your Polymarket history and in
                BetMimic&apos;s Positions page. You can close things manually on Polymarket or through
                BetMimic&apos;s manual trading tools.
              </p>
            </div>
            <div>
              <p className="font-medium">What happens if a trader I copy disappears?</p>
              <p>
                Your existing positions stay on-chain; BetMimic stops receiving new trades from that
                wallet. You can close out remaining positions manually or via your own risk rules.
              </p>
            </div>
          </div>
        </section>

        {/* CTA / Internal links */}
        <section className="space-y-3">
          <p className="text-sm text-dark-300">
            Ready to turn elite Polymarket traders into an automated strategy instead of a feed you doom
            scroll? Connect a wallet, pick a trader, and let BetMimic handle the execution and PnL math.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/traders"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
            >
              <Users className="w-4 h-4" />
              Browse Polymarket traders to copy
            </Link>
            <Link
              to="/tutorial"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              Learn how BetMimic works under the hood
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
